import React, { useState } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent
} from '@ionic/react';
import { trashOutline, codeSlashOutline, documentTextOutline, bulbOutline } from 'ionicons/icons';
import { useConfig } from '../hooks/useConfig';
import { useCodeExecution } from '../hooks/useCodeExecution';
import CodeEditor from '../components/CodeEditor';
import OutputPanel from '../components/OutputPanel';
import SEO from '../components/SEO';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import { seoService } from '../services/SEOService';
import './CodePlayground.css';

/**
 * CodePlayground Page
 * Free coding environment with modern UI
 */
function CodePlayground() {
  const { config } = useConfig();
  const { execute, output, error, isRunning, executionTime, clearOutput } = useCodeExecution();
  const [code, setCode] = useState(getDefaultCode());
  const [input, setInput] = useState('');
  const [hasExecuted, setHasExecuted] = useState(false);

  function getDefaultCode() {
    const language = config?.app?.language || 'python';
    const templates = {
      python: '# Welcome to Python Playground!\n\nprint("Hello, World!")\n',
      javascript: '// Welcome to JavaScript Playground!\n\nconsole.log("Hello, World!");\n',
      java: '// Welcome to Java Playground!\n\npublic class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, World!");\n    }\n}\n',
      cpp: '// Welcome to C++ Playground!\n\n#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << "Hello, World!" << endl;\n    return 0;\n}\n',
      c: '// Welcome to C Playground!\n\n#include <stdio.h>\n\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}\n'
    };
    return templates[language] || templates.python;
  }

  const handleRun = async (codeToRun) => {
    soundService.playClick();
    hapticService.light();
    setHasExecuted(true);
    const result = await execute(codeToRun, input);
    
    // Play feedback based on result
    if (result?.error) {
      soundService.playError();
      hapticService.error();
    } else {
      soundService.playSuccess();
      hapticService.success();
    }
  };

  const handleClear = () => {
    soundService.playClick();
    hapticService.light();
    setCode(getDefaultCode());
    setInput('');
    setHasExecuted(false);
    clearOutput();
  };

  const templates = [
    { label: '👋 Hello World', value: 'hello' },
    { label: '📦 Variables', value: 'variables' },
    { label: '🔄 Loop', value: 'loop' },
    { label: '⚡ Function', value: 'function' },
    { label: '⌨️ Input', value: 'input' }
  ];

  const loadTemplate = (templateId) => {
    if (!templateId) return;
    soundService.playClick();
    hapticService.light();
    
    const language = config?.app?.language || 'python';
    const templateCode = {
      python: {
        hello: 'print("Hello, World!")',
        variables: 'name = "Alice"\nage = 25\nprint(f"My name is {name} and I am {age} years old")',
        loop: 'for i in range(5):\n    print(f"Count: {i}")',
        function: 'def greet(name):\n    return f"Hello, {name}!"\n\nprint(greet("World"))',
        input: 'name = input("Enter your name: ")\nprint(f"Hello, {name}!")'
      },
      javascript: {
        hello: 'console.log("Hello, World!");',
        variables: 'const name = "Alice";\nconst age = 25;\nconsole.log(`My name is ${name} and I am ${age} years old`);',
        loop: 'for (let i = 0; i < 5; i++) {\n    console.log(`Count: ${i}`);\n}',
        function: 'function greet(name) {\n    return `Hello, ${name}!`;\n}\n\nconsole.log(greet("World"));',
        input: '// Note: prompt() works in browser environment\nconst name = "User";\nconsole.log(`Hello, ${name}!`);'
      },
      java: {
        hello: 'public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, World!");\n    }\n}',
        variables: 'public class Main {\n    public static void main(String[] args) {\n        String name = "Alice";\n        int age = 25;\n        System.out.println("My name is " + name + " and I am " + age + " years old");\n    }\n}',
        loop: 'public class Main {\n    public static void main(String[] args) {\n        for (int i = 0; i < 5; i++) {\n            System.out.println("Count: " + i);\n        }\n    }\n}',
        function: 'public class Main {\n    public static String greet(String name) {\n        return "Hello, " + name + "!";\n    }\n    public static void main(String[] args) {\n        System.out.println(greet("World"));\n    }\n}',
        input: 'import java.util.Scanner;\n\npublic class Main {\n    public static void main(String[] args) {\n        Scanner scanner = new Scanner(System.in);\n        System.out.print("Enter your name: ");\n        String name = scanner.nextLine();\n        System.out.println("Hello, " + name + "!");\n    }\n}'
      },
      cpp: {
        hello: '#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << "Hello, World!" << endl;\n    return 0;\n}',
        variables: '#include <iostream>\n#include <string>\nusing namespace std;\n\nint main() {\n    string name = "Alice";\n    int age = 25;\n    cout << "My name is " << name << " and I am " << age << " years old" << endl;\n    return 0;\n}',
        loop: '#include <iostream>\nusing namespace std;\n\nint main() {\n    for (int i = 0; i < 5; i++) {\n        cout << "Count: " << i << endl;\n    }\n    return 0;\n}',
        function: '#include <iostream>\n#include <string>\nusing namespace std;\n\nstring greet(string name) {\n    return "Hello, " + name + "!";\n}\n\nint main() {\n    cout << greet("World") << endl;\n    return 0;\n}',
        input: '#include <iostream>\n#include <string>\nusing namespace std;\n\nint main() {\n    string name;\n    cout << "Enter your name: ";\n    getline(cin, name);\n    cout << "Hello, " << name << "!" << endl;\n    return 0;\n}'
      },
      c: {
        hello: '#include <stdio.h>\n\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}',
        variables: '#include <stdio.h>\n\nint main() {\n    char name[] = "Alice";\n    int age = 25;\n    printf("My name is %s and I am %d years old\\n", name, age);\n    return 0;\n}',
        loop: '#include <stdio.h>\n\nint main() {\n    for (int i = 0; i < 5; i++) {\n        printf("Count: %d\\n", i);\n    }\n    return 0;\n}',
        function: '#include <stdio.h>\n\nvoid greet(char name[]) {\n    printf("Hello, %s!\\n", name);\n}\n\nint main() {\n    greet("World");\n    return 0;\n}',
        input: '#include <stdio.h>\n\nint main() {\n    char name[50];\n    printf("Enter your name: ");\n    scanf("%s", name);\n    printf("Hello, %s!\\n", name);\n    return 0;\n}'
      }
    };
    
    const langTemplates = templateCode[language] || templateCode.python;
    setCode(langTemplates[templateId] || getDefaultCode());
    setHasExecuted(false);
    clearOutput();
  };

  const language = config?.app?.language || 'python';
  const languageDisplay = language.charAt(0).toUpperCase() + language.slice(1);

  // Build result object for OutputPanel
  const result = hasExecuted ? {
    output: output || '',
    error: error,
    success: !error,
    executionTime: executionTime
  } : null;

  const meta = seoService.getPageMeta('playground');

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
      />
      <IonPage className="playground-page">
        <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Code Playground</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="playground-content">
        <div className="playground-container">

          {/* Controls Section */}
          <IonCard className="controls-card">
            <IonCardContent className="controls-content">
              <div className="controls-row">
                {/* Template Selector */}
                <div className="template-selector">
                  <IonIcon icon={documentTextOutline} className="control-icon" />
                  <IonSelect 
                    placeholder="Load Template"
                    onIonChange={e => loadTemplate(e.detail.value)}
                    interface="popover"
                    className="template-select"
                  >
                    {templates.map(t => (
                      <IonSelectOption key={t.value} value={t.value}>
                        {t.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </div>

                {/* Clear Button */}
                <IonButton 
                  fill="outline" 
                  color="danger" 
                  size="default"
                  onClick={handleClear}
                  className="clear-btn"
                >
                  <IonIcon icon={trashOutline} slot="start" />
                  Clear
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Code Editor */}
          <div className="editor-section">
            <CodeEditor
              code={code}
              language={language}
              onChange={setCode}
              onRun={handleRun}
              editable={true}
              runnable={true}
              running={isRunning}
              height={280}
            />
          </div>

          {/* Input Section (for languages that support it) */}
          {config?.execution?.allowInput && (
            <IonCard className="input-card">
              <IonCardContent>
                <div className="input-header">
                  <span className="input-label">📥 Program Input (stdin)</span>
                </div>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter input for your program here..."
                  rows={3}
                  className="input-textarea"
                />
              </IonCardContent>
            </IonCard>
          )}

          {/* Output Panel */}
          <OutputPanel
            output={result?.output || ''}
            error={result?.error}
            success={result?.success ?? true}
            executionTime={result?.executionTime}
            visible={result !== null}
          />

          {/* Tips Card */}
          <IonCard className="tips-card">
            <IonCardContent>
              <div className="tips-header">
                <IonIcon icon={bulbOutline} className="tips-icon" />
                <span className="tips-title">Pro Tips</span>
              </div>
              <ul className="tips-list">
                <li>
                  <kbd>Ctrl</kbd> + <kbd>Enter</kbd> — Run your code instantly
                </li>
                <li>
                  Use templates to get started with common patterns
                </li>
                <li>
                  Your code is stored in memory (not saved permanently)
                </li>
              </ul>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default CodePlayground;